import type {ReactElement} from 'react';
import {useState} from 'react';
import React from 'react';
import styles from './ai-prompt-box-attachment-preview.module.scss';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize} from '@Components/icon-v2/icon.types';
import type {AiPromptBoxAttachmentPreviewProps} from '@Components/ai-prompt-box/components/ai-prompt-box-attachment-preview/ai-prompt-box-attachment-preview.types';
import {AiPromptBoxSize} from '@Components/ai-prompt-box/ai-prompt-box.types';
import {isVideoUrl} from '@Utils/media.util';
import {PendingUploadStatus} from '@Components/ai-prompt-box/components/ai-prompt-box-attachments/ai-prompt-box-attachments.types';
import {CircularProgressLoader, LOADER_COLOR, LOADER_SIZE} from '@Components/circular-progress-loader';

const SIZE_DIMENSION_MAP = {
  [AiPromptBoxSize.SMALL]: 48,
  [AiPromptBoxSize.MEDIUM]: 56,
  [AiPromptBoxSize.SINGLE_LINE]: 48,
  [AiPromptBoxSize.SMALL_EXTENDED]: 48,
};

const SIZE_CLASS_MAP = {
  [AiPromptBoxSize.SMALL]: styles.small,
  [AiPromptBoxSize.MEDIUM]: styles.medium,
  [AiPromptBoxSize.SINGLE_LINE]: styles.small,
  [AiPromptBoxSize.SMALL_EXTENDED]: styles.small,
};

export function AiPromptBoxAttachmentPreview({previewUrl, index, onRemove, onPreviewClick, onRetry, size, pendingUpload, isRemovalLocked}: AiPromptBoxAttachmentPreviewProps): ReactElement {
  const [loaded, setLoaded] = useState(false);

  const dimension = SIZE_DIMENSION_MAP[size];
  const isVideo = isVideoUrl(previewUrl);
  const isUploading = pendingUpload?.status === PendingUploadStatus.UPLOADING;
  const isFailed = pendingUpload?.status === PendingUploadStatus.FAILED;
  const showOverlay = isFailed || isUploading || !loaded;
  const showOverlayDim = isFailed || isUploading;
  const failedClass = isFailed ? styles.failed : '';

  const handleMediaLoad = (): void => {
    setLoaded(true);
  };

  const handleMediaClick = (): void => {
    onPreviewClick(index);
  };

  const handleRemoveClick = (): void => {
    onRemove(index);
  };

  const handleRetryClick = (): void => {
    onRetry?.(index);
  };

  const renderMedia = (): ReactElement => {
    const commonClassName = `${styles.attachmentItem} ${loaded ? '' : '_hidden'} ${failedClass}`;

    if (isVideo) {
      return <video src={previewUrl} className={commonClassName} onLoadedData={handleMediaLoad} onClick={handleMediaClick} muted />;
    }

    return <img src={previewUrl} className={commonClassName} onLoad={handleMediaLoad} onClick={handleMediaClick} alt="" />;
  };

  const renderOverlayContent = (): ReactElement => {
    if (isFailed && onRetry) {
      return (
        <Icon
          className={styles.retryButton}
          icon={'icon-rotate-cw'}
          size={IconSize.SIZE_ICON_20}
          shape={IconShape.NONE}
          ariaLabel={window.i18next.t('pmwjs_retry_upload')}
          onClick={handleRetryClick}
        />
      );
    }
    return <CircularProgressLoader color={LOADER_COLOR.DARK} size={LOADER_SIZE.XXSMALL} classes="_unmargin" />;
  };

  const renderOverlay = (): ReactElement | null => {
    if (!showOverlay) {
      return null;
    }
    return (
      <div className={`${styles.uploadingOverlay} ${showOverlayDim ? styles.uploadingOverlayDim : ''} ${failedClass}`}>
        {renderOverlayContent()}
      </div>
    );
  };

  const renderRemoveButton = (): ReactElement | null => {
    if (isRemovalLocked) {
      return null;
    }
    return (
      <Icon
        className={styles.removeAttachmentButton}
        icon={'icon-close'}
        size={IconSize.SIZE_ICON_8}
        shape={IconShape.CIRCLE}
        onClick={handleRemoveClick}
      />
    );
  };

  return (
    <div className={`${styles.attachment} ${SIZE_CLASS_MAP[size]}`}>
      <div
        className={`${styles.attachmentItem} ${styles.placeholder} ${failedClass} ${loaded ? '_hidden' : ''} spacing-m-b-2`}
        style={{width: dimension, height: dimension}}
      />
      {renderMedia()}
      {renderOverlay()}
      {renderRemoveButton()}
    </div>
  );
}

export default React.memo(AiPromptBoxAttachmentPreview, (prevProps, nextProps) => {
  return (
    prevProps.previewUrl === nextProps.previewUrl &&
    prevProps.size === nextProps.size &&
    prevProps.index === nextProps.index &&
    prevProps.pendingUpload?.status === nextProps.pendingUpload?.status &&
    prevProps.isRemovalLocked === nextProps.isRemovalLocked
  );
});
